import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  ChartBarIcon,
  Cog6ToothIcon,
  KeyIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { TopHeaderCompanyInfo } from './TopHeaderCompanyInfo';
import { NotificationDropdown } from './NotificationDropdown';
import { TopHeaderTasksPanel } from './TopHeaderTasksPanel';

type TopHeaderProps = {
  onToggleSidebar?: () => void;
};

export function TopHeader({ onToggleSidebar }: TopHeaderProps) {
  const { profile, logout } = useAuth();
  const { isOrganizationAdmin } = usePermissions();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const displayName = profile?.nickName || profile?.fullName || 'usuário';
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="relative shrink-0 border-b border-primary-500/20 bg-baruk-surface text-secondary-800">
      <div className="flex items-center justify-between gap-4 px-4 py-2.5 md:px-6">
      <div className="flex min-w-0 flex-1 items-center gap-2 md:gap-4">
        <button
          type="button"
          className="shrink-0 rounded-xl p-2 text-primary-700 hover:bg-primary-500/10 lg:hidden"
          onClick={onToggleSidebar}
          aria-label="Abrir menu"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>

        <TopHeaderCompanyInfo />
      </div>

      <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <TopHeaderTasksPanel />
          <NotificationDropdown />

          <Link
            to="/performance"
            className="hidden items-center gap-1.5 rounded-xl px-2 py-1.5 text-sm font-medium text-secondary-700 hover:bg-primary-500/10 sm:inline-flex"
            title="Desempenho"
          >
            <ChartBarIcon className="h-5 w-5 text-primary-600" />
            <span>Desempenho</span>
          </Link>

          <div className="relative" ref={menuRef}>
            <button
              type="button"
              className="flex items-center gap-2 rounded-xl px-2 py-1 text-secondary-800 hover:bg-primary-500/10 sm:gap-3"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="hidden whitespace-nowrap text-sm text-secondary-600 sm:inline">
                Olá, <span className="font-semibold text-secondary-900">{displayName}</span>
              </span>
              {profile?.profilePhotoBase64 ? (
                <img
                  src={profile.profilePhotoBase64}
                  alt=""
                  className="h-9 w-9 rounded-full object-cover ring-2 ring-primary-500/20"
                />
              ) : (
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white ring-2 ring-primary-500/15">
                  {initials || 'U'}
                </span>
              )}
            </button>

            {menuOpen ? (
              <div className="absolute right-0 z-30 mt-2 w-56 rounded-2xl border border-secondary-200 bg-white py-2 text-secondary-800 shadow-lg">
                <Link
                  to="/performance"
                  className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-secondary-50 sm:hidden"
                  onClick={() => setMenuOpen(false)}
                >
                  <ChartBarIcon className="h-5 w-5 text-primary-600" />
                  Desempenho
                </Link>
                <Link
                  to="/settings/profile"
                  className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-secondary-50"
                  onClick={() => setMenuOpen(false)}
                >
                  <UserCircleIcon className="h-5 w-5 text-primary-600" />
                  Editar perfil
                </Link>
                <Link
                  to="/settings/profile#password"
                  className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-secondary-50"
                  onClick={() => setMenuOpen(false)}
                >
                  <KeyIcon className="h-5 w-5 text-primary-600" />
                  Alterar senha
                </Link>
                {isOrganizationAdmin ? (
                  <Link
                    to="/settings/company"
                    className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-secondary-50"
                    onClick={() => setMenuOpen(false)}
                  >
                    <Cog6ToothIcon className="h-5 w-5 text-primary-600" />
                    Configurações
                  </Link>
                ) : null}
                <hr className="my-2 border-secondary-100" />
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                  onClick={() => {
                    setMenuOpen(false);
                    void logout();
                  }}
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                  Sair
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}
